import {
    SportsBasketball as AthleteIcon,
    School as CoachIcon,
    FamilyRestroom as GuardianIcon,
    Business as OrgIcon
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Container, Fade, Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import SignupModal from "../components/SignupModal";
import TestimonialCard from "../components/TestimonialCard";
import type { Testimonial } from "../content/roleFaqsContent";

export const ROLES = [
    { slug: "athlete", label: "ATHLETE", icon: <AthleteIcon />, path: "/faqs/athlete", image: "/images/athlete.png" },
    { slug: "guardian", label: "GUARDIAN", icon: <GuardianIcon />, path: "/faqs/guardian", image: "/images/guardian.png" },
    { slug: "coach", label: "COACH", icon: <CoachIcon />, path: "/faqs/coach", image: "/images/coach.png" },
    { slug: "organization", label: "ORGANIZATION", icon: <OrgIcon />, path: "/faqs/organization", image: "/images/organizations.png" }
];

export const TESTIMONIALS: Testimonial[] = [
    {
        quote: "Something like SMSP which prepares kids to be mentally strong along with being physically prepared has been the missing link.",
        author: "B. Steele",
        role: "Athletic Director"
    },
    {
        quote: "I love this site and the Bell family is excited to have this resource ready for our young athlete.",
        author: "K. Bell",
        role: "Guardian of student athlete"
    },
    {
        quote: "In my previous work as an educator I often wished student athletes had access to resources like these to help them perform at their best while truly enjoying the sport they are involved in.",
        author: "G. Gordon",
        role: "Guardian of student athlete / Former Educator"
    },
    {
        quote: "My daughter quit basketball during COVID and could have used this program to boost her confidence during that isolated time.",
        author: "C. Johnson",
        role: "Guardian of student athlete"
    },
    {
        quote: "Super valuable for the kids. Belief is where it starts and I could have made it a lot further in the game if I wasn't in my head.",
        author: "Anonymous",
        role: "High School Basketball Coach"
    }
];

/**
 * Shared body content for both HomeSmsp and HomeWhiteLabel (Why it Works, Select your role, Testimonials, Final CTA).
 */
const HomePageBody: React.FC = () => {
    const [signupModalOpen, setSignupModalOpen] = useState(false);

    return (
        <>
            {/* Why it Works */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container alignItems="center" spacing={4} direction={{ xs: "column-reverse", md: "row" }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: "bold", textAlign: { xs: "center", md: "left" } }}>
                            Why it Works
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                lineHeight: 1.7,
                                mb: 2,
                                fontSize: "1.1rem",
                                textAlign: { xs: "center", md: "left" }
                            }}
                        >
                            Confidence isn't something you either have or don't—it's something you build. Our program is designed to teach athletes how to mentally prepare, emotionally recover, and lead from within.
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                lineHeight: 1.7,
                                color: "text.secondary",
                                textAlign: { xs: "center", md: "left" }
                            }}
                        >
                            Whether you're coming off a tough game or gearing up for your biggest season yet, Strong Mind Strong Performance provides the system, support, and structure needed to rise.
                        </Typography>
                        <Box sx={{ textAlign: { xs: "center", md: "left" }, mt: 4 }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => setSignupModalOpen(true)}
                                sx={{ px: 4, py: 1.5, fontWeight: "bold" }}
                            >
                                GET STARTED
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            component="img"
                            src="/images/Storm+Hurdle.jpeg"
                            alt="Young athlete clearing a hurdle at a track meet"
                            sx={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                                borderRadius: 2,
                                objectFit: "cover"
                            }}
                        />
                    </Grid>
                </Grid>
            </Container>

            {/* Select your role – cards linking to role FAQs */}
            <Box id="select-your-role" sx={{ py: 8, bgcolor: "action.hover" }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ textAlign: "center", mb: 4, fontWeight: "bold", fontStyle: "italic" }}
                    >
                        Select your role below to explore your path
                    </Typography>
                    <Grid container spacing={3}>
                        {ROLES.map((role, index) => (
                            <Grid item xs={12} sm={6} md={3} key={role.slug}>
                                <Fade in timeout={400 + index * 100}>
                                    <Box component="span" sx={{ display: "block", height: "100%" }}>
                                        <Card
                                            component={Link}
                                            to={role.path}
                                            sx={{
                                                position: "relative",
                                                height: "100%",
                                                minHeight: 220,
                                                textDecoration: "none",
                                                color: "inherit",
                                                display: "flex",
                                                flexDirection: "column",
                                                overflow: "hidden",
                                                backgroundImage: `url(${role.image})`,
                                                backgroundSize: "cover",
                                                backgroundPosition: "center right",
                                                transition: "transform 0.2s, boxShadow 0.2s",
                                                "&:hover": {
                                                    transform: "translateY(-4px)",
                                                    boxShadow: 4
                                                }
                                            }}
                                        >
                                            {/* Dark overlay for readability */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    bgcolor: "rgba(0,0,0,0.45)",
                                                    "&:hover": { bgcolor: "rgba(0,0,0,0.35)" }
                                                }}
                                            />
                                            <CardContent
                                                sx={{
                                                    position: "relative",
                                                    flexGrow: 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "white",
                                                    textAlign: "center",
                                                    py: 3,
                                                    "&:last-child": { pb: 3 }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        fontSize: 64,
                                                        lineHeight: 1,
                                                        mb: 1.5,
                                                        "& .MuiSvgIcon-root": { fontSize: "inherit" }
                                                    }}
                                                >
                                                    {role.icon}
                                                </Box>
                                                <Typography variant="h6" sx={{ fontWeight: "bold", letterSpacing: 1 }}>
                                                    {role.label}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Testimonials */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" component="h2" sx={{ textAlign: "center", mb: 6, fontWeight: "bold" }}>
                        What People Are Saying
                    </Typography>
                    <Grid container spacing={4}>
                        {TESTIMONIALS.map((t, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Fade in timeout={600 + index * 150}>
                                    <Box component="span" sx={{ display: "block" }}>
                                        <TestimonialCard testimonial={t} elevation={2} />
                                    </Box>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box sx={{ bgcolor: "primary.main", color: "white", py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h5" component="h2" sx={{ textAlign: "center", mb: 4, fontWeight: "bold" }}>
                        Ready to build your athlete's confidence?
                    </Typography>
                    <Box sx={{ textAlign: "center" }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => setSignupModalOpen(true)}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontWeight: "bold",
                                bgcolor: "white",
                                color: "primary.main",
                                "&:hover": { bgcolor: "grey.100" }
                            }}
                        >
                            GET STARTED
                        </Button>
                    </Box>
                </Container>
            </Box>

            <SignupModal open={signupModalOpen} onClose={() => setSignupModalOpen(false)} />
        </>
    );
};

export default HomePageBody;
